//Libraries
import React, {FC, useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Paper, Stack, Alert, Divider, Grid} from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import PlaceIcon from '@mui/icons-material/Place';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

//Providers
import {useOrgService} from 'src/services';
import { useReferences } from 'src/services';
import {useAuth} from 'src/contexts';

//Interfaces & Typing
import {IDIRecord} from 'src/types/org/di/diInterface';
import {IOpsRequest} from 'src/types/org/operation/requestInterface';
import {ISummary} from 'src/types';
import {IOption} from 'src/generic/component/form';

//Processing
import {loadSummaries} from 'src/services/schoolRequest';
import {formatDate} from 'src/utils';
import {getFile} from 'src/services/schoolRequest';
import {formatAddress} from 'src/generic/component/address';
import {formatContact} from 'src/generic/component/contact';
import {ChangeManager} from 'src/generic/utils/form';

//Components
import {Title, Loader, Highlight, FieldNote, AntSwitch, DataValue} from 'src/generic/component';
import {SubTitle} from 'src/generic/component';
import {Button, PrimaryButton} from "@cb/apricot-react";
import {APP_URLS} from "../../constants";

const DataField: FC<{ name: string | React.ReactNode, value: string | React.ReactNode; }> = ({name, value}) => {
    return <div style={{display: 'flex', marginBottom: '2px'}}>
        <div style={{width: '250px'}}>
            {name}
        </div>
        <DataValue style={{
            // textAlign: 'right', flexGrow: 1
        }}>
            {value}
    </DataValue>
  </div>;
};

const useDi = (orgId: string, diCode: string) => {
    const [data, setData] = useState<IDIRecord | undefined>();
    const [isLoading, setLoading] = useState<boolean>(false);
    const service = useOrgService();
  
    useEffect(() => {
      (async () => {
        setLoading(true);
        try {
          const di = await service.findDiAdmin(orgId);
          setData(di);
        } catch (error) {
  
        }
        setLoading(false);
      })();
    }, [diCode,orgId]);
  
    return { data, isLoading };
  };

  const useHistory = (orgId: string, diCode: string) => {
    const [data, setData] = useState<ISummary[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);
  
    useEffect(() => {
      (async () => {
        setLoading(true);
        try {
          const summaries = await loadSummaries({});
          const history = summaries.all().filter((summary) => {
            return summary.processStatus === 'completed' && summary.orgId === orgId && summary.diCode === diCode;
          });
          setData(history);
        } catch (error) {
  
        }
        setLoading(false);
      })();
    }, [diCode, orgId]);
  
    return { data, isLoading };
  };

  interface IHistoryDetailsProps {
    summary: ISummary;
  }

  const useSaveRecord = (summary: ISummary) => {
    const [data, setData] = useState<IOpsRequest<IDIRecord> | undefined>();
    const [isLoading, setLoading] = useState<boolean>(false);
  
    useEffect(() => {
      (async () => {
        setLoading(true);
        try {
          if (summary.operationSubmission) {
            const request = await getFile(summary.operationSubmission);
            setData(request);
          }
        } catch (error) {
  
        }
        setLoading(false);
      })();
    }, [summary]);
  
    return { data, isLoading };
  
  };

  const getLabel = (key: any, map: IOption[]) => {
    return map.find((item) => item.key === key)?.label;
  };

  const orgGeneralFields = ['org.name', 'org.nameShort', 'org.nameAbbr', 'org.email', 'org.phone', 'org.fax', 'org.visitingAddress'];
  const orgTypeFields = ['org.orgTypeCd', 'org.orgSubTypeCd', 'org.campus', 'org.charterInd', 'org.educationDeliveryMethodCds'];
  const orgProprietaryFields = ['org.mdrPid', 'org.ipedsId', 'org.ncesId', 'org.feeWaiverId'];
  const diGeneralFields = ['di.diCode', 'di.apEligibilityInd', 'di.satEligibilityInd'];
  const diCondiitonalFields = ['di.satContact','di.apContact', 'di.apAddress'];

  const DELIVERY_METHOD_OPTIONS: any = {
    5: 'Classroom',
    4: 'Internet'
  }

  const HistoryDetails: FC<IHistoryDetailsProps> = ({summary}) => {
    const {roles} = useAuth();
    const isAdmin = roles.includes('XPM_ADMIN');
    const showProtected = isAdmin;

    const {data, isLoading: recLoading} = useSaveRecord(summary);
    const {types, subTypes, instLevels, isLoading: refLoading} = useReferences();

    const isLoading = recLoading || refLoading;

    let details = <Loader></Loader>;
    if (!isLoading && data) {
    const { isChanged, getCurrent, getChange, getChangeColor } = ChangeManager<IDIRecord>(data.original || {}, data.request);

    const hasSectionChanges = (fields: string[]) => {
      for (const field of fields) {
        if (isChanged(field)) {
          return true;
        }
      }
      return false;
    };

    const BuildChange = (field: string, map?: IOption[]) => {
      const current = (map ? getLabel(getCurrent(field), map) : getCurrent(field));
      const change = (map ? getLabel(getChange(field), map) : getChange(field));
      console.log(field, change);
      const color = getChangeColor(field);
      const format = (value: any) => {
        if (typeof value === 'boolean') {
          return value === true ? 'Yes' : 'No';
        } else {
          return value || '(empty)';
        }
      };

      return <>
        <div style={{ display: 'inline-block' }} >{format(current)}</div>
        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
        <div style={{ display: 'inline-block' }} className={color || ''}>{format(change)}</div>
      </>;
    };

    const { org} = data?.request || {};
    const {di} = data?.request || {};
    const { visitingAddress } = org || {};
    const { satContact, apContact, apAddress } = di || {};

    details = <div>
      <h6 style={{ marginBottom: '15px' }}>Organization Profile</h6>
      <Paper elevation={2} style={{ padding: '20px', marginBottom: '15px' }}>
        {hasSectionChanges(orgGeneralFields.concat(orgTypeFields, orgProprietaryFields)) ?
          <>
            {hasSectionChanges(orgGeneralFields) &&
              <>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('org.name') &&
                    <DataField name='Name' value={BuildChange('org.name')}></DataField>
                  }

                  {isChanged('org.nameShort') &&
                    <DataField name='Short Name' value={BuildChange('org.nameShort')}></DataField>
                  }

                  {isChanged('org.nameAbbr') &&
                    <DataField name='Abbreviated Name' value={BuildChange('org.nameAbbr')}></DataField>
                  }

                  {isChanged('org.website') &&
                    <DataField name='Website' value={BuildChange('org.website')}></DataField>
                  }

                  {isChanged('org.phone.formatted') &&
                    <DataField name='Phone' value={BuildChange('org.phone.formatted')}></DataField>
                  }

                  {isChanged('org.fax.formatted') &&
                      <DataField name='Fax' value={BuildChange('org.fax.formatted')}></DataField>
                  }

                  {isChanged('org.email') &&
                    <DataField name='Email' value={BuildChange('org.email')}></DataField>
                  }
                </div>

                <div>
                  {isChanged('org.visitingAddress') &&
                    <DataField name='Visiting Address' value={<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        {getCurrent('org.visitingAddress') ? formatAddress(getCurrent('org.visitingAddress')) : '(empty)'}
                      </div>
                      <div>
                        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
                      </div>
                      <div className={getChangeColor('org.visitingAddress')}>
                        {visitingAddress ? formatAddress(visitingAddress) : '(empty)'}
                      </div>
                    </div>}></DataField>
                  }
                </div>

              </>
            }

            {hasSectionChanges(orgTypeFields) &&
              <>
                <div style={{ marginBottom: '8px' }}>
                  <SubTitle>Type Information</SubTitle>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('org.orgTypeCd') &&
                    <DataField name='Type' value={BuildChange('org.orgTypeCd', types.map((item) => ({ key: parseInt(item.key), label: item.label })))}></DataField>
                  }

                  {isChanged('org.orgSubTypeCd') &&
                    <DataField name='Sub Type' value={BuildChange('org.orgSubTypeCd', subTypes.map((item) => ({ key: parseInt(item.key), label: item.label })))}></DataField>
                  }

                  {isChanged('org.campus') &&
                    <DataField name='Campus' value={BuildChange('org.campus')}></DataField>
                  }
                  {isChanged('org.charterInd') &&
                    <DataField name='Charter' value={BuildChange('org.charterInd')}></DataField>
                  }
                </div>
              </>
            }

            {(showProtected && hasSectionChanges(orgProprietaryFields)) &&
              <div>
                <div style={{ marginBottom: '8px' }}>
                  <SubTitle>Proprietary IDs</SubTitle>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('org.mdrPid') &&
                    <DataField name='MDR PID' value={BuildChange('org.mdrPid')}></DataField>
                  }

                  {isChanged('org.ipedsId') &&
                    <DataField name='IPEDS ID' value={BuildChange('org.ipedsId')}></DataField>
                  }
                  {isChanged('org.ncesId') &&
                    <DataField name='NCES ID' value={BuildChange('org.ncesId')}></DataField>
                  }
                  {isChanged('org.feeWaiverId') &&
                    <DataField name='Fee Waiver ID' value={BuildChange('org.feeWaiverId')}></DataField>
                  }
                </div>
              </div>
            }
          </> : <FieldNote>No Changes</FieldNote>
        }
      </Paper>

      <h6 style={{ marginBottom: '15px' }}>Code Information</h6>
      <Paper elevation={2} style={{ padding: '20px', marginBottom: '15px' }}>
        {hasSectionChanges(diGeneralFields) ?
          <>
            {hasSectionChanges(diGeneralFields) &&
              <>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('di.diCode') &&
                    <DataField name='Score Send Code' value={BuildChange('di.diCode')}></DataField>
                  }
                  {isChanged('di.satEligibilityInd') &&
                    <DataField name='SAT Program Accepted' value={BuildChange('di.satEligibilityInd')}></DataField>
                  }
                  {isChanged('di.satContact') &&
                    <DataField name='SAT Contact Information' value={<div style={{marginBottom: '20px' }}>
                      <div>
                        {getCurrent('di.satContact') ? formatContact(getCurrent('di.satContact')) : '(empty)'}
                      </div>
                      <div>
                        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
                      </div>
                      <div className={getChangeColor('di.satContact')}>
                        {satContact ? formatContact(satContact) : '(empty)'}
                      </div>
                    </div>}></DataField>
                  }
                  {isChanged('di.apEligibilityInd') &&
                    <DataField name='AP Program Accepted' value={BuildChange('di.apEligibilityInd')}></DataField>
                  }
                  {isChanged('di.apContact') &&
                    <DataField name='AP Contact Information' value={<div style={{marginBottom: '20px' }}>
                      <div>
                        {getCurrent('di.apContact') ? formatContact(getCurrent('di.apContact')) : '(empty)'}
                      </div>
                      <div>
                        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
                      </div>
                      <div className={getChangeColor('di.apContact')}>
                        {apContact ? formatContact(apContact) : '(empty)'}
                      </div>
                    </div>}></DataField>
                  }
                  {isChanged('di.apAddress') &&
                    <DataField name='AP Address' value={<div style={{ marginBottom: '20px' }}>
                      <div>
                        {getCurrent('di.apAddress') ? formatAddress(getCurrent('di.apAddress')) : '(empty)'}
                      </div>
                      <div>
                        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
                      </div>
                      <div className={getChangeColor('di.apAddress')}>
                        {apAddress ? formatAddress(apAddress) : '(empty)'}
                      </div>
                    </div>}></DataField>
                  }
                </div>

              </>
            }
          </> : <FieldNote>No Changes</FieldNote>
        }
      </Paper>
    </div>;
  }

  const { preparedBy, approvedBy, preparedDate, approvedDate } = data?.meta || {};

  return <div>
    <div style={{ marginBottom: '20px' }}>
      <div style={{ marginBottom: '5px' }}>
          <Highlight>Type: {summary.requestType.toUpperCase() === 'INTERNALUPDATE' ? 'UPDATE' : summary.requestType.toUpperCase()}</Highlight>
          <Highlight>Updated At: {formatDate(summary.updateDate)}</Highlight>
      </div>

      <div style={{ lineHeight: '18px' }}>
        <FieldNote>Prepared by {preparedBy} @ {formatDate(preparedDate || 0)}</FieldNote>
      </div>
      <div style={{ lineHeight: '18px' }}>
        <FieldNote>Approved by {approvedBy} @ {formatDate(approvedDate || 0)}</FieldNote>
      </div>
    </div>

    {details}
  </div>;
};

interface IDiProfileProps {
    record: IDIRecord;
    original?: IDIRecord;
    showDiff?: boolean;
    onlyChanges?: boolean;
}

export const DiProfile: FC<IDiProfileProps> = ({ record }) => {
    const { roles } = useAuth();
    const { types, subTypes, instLevels, isLoading } = useReferences();
    const org = record.org || {};
    const di = record.di || {};
  
    const isAdmin = roles.includes('XPM_ADMIN');
    const showProtected = isAdmin;
    
    const deliveryMethodMap = (deliveryMethod: any[]) => {
          return deliveryMethod.map(dm => {
              const dmm = dm;
              return DELIVERY_METHOD_OPTIONS[dmm];
          }).toString().replace(',', ', ');
      }
  
      const navigate = useNavigate();

      const editDiProfile = (record: IDIRecord) => {
        navigate(APP_URLS.DIPROFILE_EDIT, {
            state: {
                record: record,
                type: 'di'
            }
        });
    };
  
      if (isLoading) {
          return <Loader></Loader>;
      } else {
          return <div style={{display: 'inline-block'}}>
              <Title>Organization Profile</Title>
              <Paper elevation={2} style={{padding: '20px', marginBottom: '15px'}}>
                  <div style={{marginBottom: '15px'}}>
                      <DataField name='Name' value={org.name}></DataField>
                      <DataField name='Short Name' value={org.nameShort}></DataField>
                      <DataField name='Abbreviated Name' value={org.nameAbbr || 'N/A'}></DataField>
                      <DataField name='Website' value={org.website || 'N/A'}></DataField>
                      <DataField name='Phone' value={org.phone?.formatted || 'N/A'}></DataField>
                      <DataField name='Fax' value={org.fax?.formatted || 'N/A'}></DataField>
                      <DataField name='Email' value={org.email || 'N/A'}></DataField>
                  </div>
  
                  <div style={{marginBottom: '20px'}}>
            <DataField name={<>Visiting Address <PlaceIcon sx={{ fontSize: 16, color: '#bb2020' }}></PlaceIcon></>} value={formatAddress(org.visitingAddress)}></DataField>
          </div>
  
          <div style={{ marginBottom: '8px' }}>
            <SubTitle>Type Information</SubTitle>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <DataField name='Type' value={(types.find((item) => parseInt(item.key) === org.orgTypeCd)?.label) || ''}></DataField>
            <DataField name='Sub-Type' value={(subTypes.find((item) => parseInt(item.key) === org.orgSubTypeCd)?.label) || ''}></DataField>
            <DataField name='Institution Level' value={(instLevels.find((item) => parseInt(item.key) === org.instLvlCd)?.label) || ''}></DataField>
            <DataField name='Campus' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={org.campus || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>
  
              <DataField name='Charter' value={<Stack direction="row" spacing={1} alignItems="center">
                  <div>No</div>
                  <AntSwitch checked={org.charterInd || false}></AntSwitch>
                  <div>Yes</div>
              </Stack>}></DataField>
              <DataField name='Delivery Method' value={deliveryMethodMap(org?.educationDeliveryMethodCds || [])}/>
          </div>
  
          {showProtected &&
            <div>
              <div style={{ marginBottom: '8px' }}>
                <SubTitle>Proprietary IDs</SubTitle>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <DataField name='MDR PID' value={org.mdrPid || 'N/A'}></DataField>
                <DataField name='IPEDS ID' value={org.ipedsId || 'N/A'}></DataField>
                <DataField name='NCES ID' value={org.ncesId || 'N/A'}></DataField>
                <DataField name='Fee Waiver ID' value={org.feeWaiverId || 'N/A'}></DataField>
              </div>
            </div>
          }
        </Paper>
  
        <Title>Code Information</Title>
        <Paper elevation={2} style={{ padding: '20px', marginBottom: '15px' }}>
          <div style={{ marginBottom: '15px' }}>
            <DataField name='Score Send Code' value={di.diCode}></DataField>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <SubTitle>Program Information</SubTitle>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '75px' }}>

          <div>
          <DataField name='SAT Program Accepted' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={di.satEligibilityInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>
          </div>
          { di.satEligibilityInd &&
          <div style={{ marginBottom: '20px' }}>
          <DataField name={<> SAT Contact Information</>} value={di.satContact ? formatContact(di.satContact) : <i>N/A</i>}></DataField>
          </div>
          }
          <div>
          <DataField name='AP Program Accepted' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={di.apEligibilityInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>
          </div>
          {di.apEligibilityInd &&
          <div>
          <div style={{ marginBottom: '20px' }}>
          <DataField name={<> AP Contact Information</>} value={di.apContact ? formatContact(di.apContact) : <i>N/A</i>}></DataField>
          </div>
          <div style={{ marginBottom: '20px' }}>
          <DataField name={<>AP Address <PlaceIcon sx={{ fontSize: 16, color: '#bb2020' }}></PlaceIcon></>} value={di.apAddress? formatAddress(di.apAddress) : <i>Same as Visiting Address</i>}></DataField>
          </div>
          </div>}

          </div>
        </Paper>
        {showProtected &&
                <div>
                    <PrimaryButton style={{width: '40%', marginBottom: '10px', marginLeft: '200px'}}
                                   onClick={() => {
                                       editDiProfile(record)
                                   }}>Edit
                    </PrimaryButton>
                </div>
        }
        </div>;
    }
  };


export const DiProfilePage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as any;
  const diCode = state?.diCode;
  const orgId = state?.orgId;
  const searchParams = state?.search;

  const { data, isLoading } = useDi(orgId, diCode);
  const { data: history, isLoading: historyLoading } = useHistory(orgId, diCode);

  if (isLoading) {
    return <Loader />;
  } else {
    return <div>
      <Grid container>
        <Grid item xs={7} style={{ paddingRight: '15px' }}>
          {data &&
            <DiProfile record={data} />
          }
        </Grid>
        <Grid item xs={5} style={{
          borderLeft: '2px solid #EEEEEE',
          paddingLeft: '15px',
          paddingRight: '15px',
          height: '100vh',
          overflowY: 'scroll'
        }}>
          <Title>Change History</Title>
          {historyLoading ?
            <Loader></Loader> :
            history.length > 0 ? history.map((item) => {
              return <>
                <HistoryDetails summary={item} />
                <Divider style={{ margin: '20px 0px' }} />
              </>;
            }) : <Alert severity='info'>No History Found</Alert>
          }
        </Grid>
        <Button style={{ width: '100%' }} onClick={() => navigate(APP_URLS.SEARCH, { state: { search: searchParams, type: 'di' } })}>Close</Button>
      </Grid>
    </div>;
  }
};